import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { airBrandMap } from '../../constants/BrandMaps';
import { Color, FontSize, CommonProperties, CommonStyles } from '../../styles';
import Images from '../../constants/Images';
import Icon from '../Icon';
import { formatCurrency, isBXcurrency } from '../../utils/currency.utils';
import { timeConvertUpperToLower } from '../../utils/time.utils';
import { getSegmentsCountText } from '../../utils/segmentsCount.util';
import { useDispatch, useSelector } from 'react-redux';
import { isAirBrandEnabled } from '../../utils/air.util';
import { selectAir } from '../../store/actions/air';
import Card from './Card';
import { cardStyle, XSmallText, CardLeftTextDetail, ShrinkView, PerUnitPrice } from './card.utils';

import BaseText from '../BaseText';
import PriceItem from './PriceItem';
import TextWithIcon from '../TextWithIcon';
import RowView from '../RowView';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../../i18n';
import moment from 'moment';
const cleanLayoverText = (text) =>
  text && text.replace('layover ', '').replace('0 h', '').replace(' m', 'm');

const getBrandDetailsByName = (name) => {
  const lowerCaseName = name ? name.toLowerCase() : '';
  return airBrandMap[lowerCaseName] || {};
};

const getBrandDetails = (brand) => {
  let name = '';

  if (!brand.LegBrandName) {
    name = brand.DisplayName.split(' ')[0];
  } else {
    name = brand.LegBrandName;
  }

  return getBrandDetailsByName(name);
};

const customBrandName = (name) => {
  const lowerCaseName = name ? name.toLowerCase() : '';
  return airBrandMap[lowerCaseName]?.key || name.replace(' ', '');
};

const getSegmentsText = (list = []) => {
  if (Array.isArray(list)) {
    return list
      .reduce((acum, curr) => {
        acum.push(curr.DepartureAirportCode);
        acum.push(curr.ArrivalAirportCode);
        return acum;
      }, [])
      .filter((value, index, array) => value !== array[index + 1])
      .join(' - ');
  }

  return ' ';
};

const getDifferenceDays = (segments) => {
  if (!segments && !Array.isArray(segments) && segments.length > 0) {
    return '';
  }

  const startDate = segments[0].DepartureDate;
  const endDate = segments[segments.length - 1].ArrivalDate;
  const days = moment(endDate).diff(moment(startDate), 'days');
  return days > 0 ? '+' + days : null;
};

const transformAirDataToCard = (flight = {}, legIndex, currency, currencyCulture) => {
  const leg = flight?.AirLegs?.[legIndex] || {};
  const policyReasonCode =
    flight.MultiBrands && flight.MultiBrands[flight.selectedBrand]?.ReasonCode?.toLowerCase();
  const flightBrand = flight.MultiBrands[flight.selectedBrand] || flight.MultiBrands[0];
  const price = isBXcurrency(currencyCulture) ? flightBrand.TotalPoints : flightBrand.TotalFare;
  return {
    title:
      timeConvertUpperToLower(leg.DepartTime) + ' - ' + timeConvertUpperToLower(leg.ArrivalTime),
    titleExtra: getDifferenceDays(leg.AirSegments),
    subtitle: getSegmentsText(leg.AirSegments),
    value: formatCurrency(price, currency, currencyCulture),
    textDownValue: leg.TotalDuration,
    icon: leg.IsMultipleAirline
      ? Images.air['36x36']['MultipleAirlinesPNG']
      : Images.air['36x36'][leg.AirlineCode],
    textDownIcon: getSegmentsCountText(leg.AirSegmentsCount - 1),
    policy: policyReasonCode,
    expandInsteadOfPress:
      flight?.MultiBrands?.[flight.selectedBrand] &&
      (flight.MultiBrands[flight.selectedBrand]?.IsSuppressed ||
        !isAirBrandEnabled(flight.MultiBrands[flight.selectedBrand])),
  };
};

const AirDetailsItem = ({ segment, leg, flight }) => {
  const { tKey } = useComponentTKey('FlightScreen');
  const { t } = useTranslation(i18nNS.air);

  return (
    <View style={CommonStyles.mb3}>
      <RowView>
        <CardLeftTextDetail />
        <ShrinkView>
          <XSmallText>{segment.AirlineName + ' #' + segment.FlightNumber}</XSmallText>
        </ShrinkView>
      </RowView>
      <RowView>
        <CardLeftTextDetail>D:</CardLeftTextDetail>
        <ShrinkView>
          <View style={cardStyle.textRow}>
            <XSmallText>{timeConvertUpperToLower(segment.DepartTime)}</XSmallText>
            <XSmallText style={[CommonStyles.ml3, cardStyle.grayColor]}>
              {segment.DepartAirportCityName}
            </XSmallText>
          </View>
          <XSmallText style={cardStyle.grayColor}>{segment.Duration}</XSmallText>
        </ShrinkView>
      </RowView>
      <RowView>
        <CardLeftTextDetail>A:</CardLeftTextDetail>
        <ShrinkView>
          <View style={cardStyle.textRow}>
            <XSmallText>{timeConvertUpperToLower(segment.ArrivalTime)}</XSmallText>
            <XSmallText style={[CommonStyles.ml3, cardStyle.grayColor]}>
              {segment.ArrivalAirportCityName}
            </XSmallText>
          </View>
          <XSmallText>{segment.CarrierDetails}</XSmallText>
          <XSmallText>{t(tKey(leg.AirLegBrandName.replace(' ', '')))}</XSmallText>
          <XSmallText>{segment.OperatedAirlineName}</XSmallText>
          <View style={[cardStyle.textRow, CommonStyles.mt2]}>
            {flight.IsCorporate && <BaseText style={styles.mark}>Corporate</BaseText>}
            <Icon
              style={[CommonStyles.py1, CommonStyles.ml2]}
              name="wifi"
              size={FontSize.xSmall}
              color={Color.primary}
            />
            <Icon
              style={[CommonStyles.py1, CommonStyles.ml2]}
              name="power"
              size={FontSize.xSmall}
              color={Color.primary}
            />
          </View>
        </ShrinkView>
      </RowView>
      {segment.LayOverTimeInfo && (
        <View style={styles.layover}>
          <TextWithIcon iconName="time">{cleanLayoverText(segment.LayOverTimeInfo)}</TextWithIcon>
        </View>
      )}
    </View>
  );
};

const AirDetails = ({
  flight = {},
  legIndex,
  showBrands,
  showPerUnitPrice,
  currency,
  currencyCulture,
  legNumber,
  setFlightLoading,
}) => {
  const { tKey } = useComponentTKey('AirCard');
  const { t } = useTranslation(i18nNS.resultCards);
  const dispatch = useDispatch();
  const leg = flight?.AirLegs?.[legIndex];
  const brands = flight.MultiBrands;
  const onBrandPressHandler = useCallback(
    async (brand) => {
      await dispatch(selectAir(flight, brand, legNumber, setFlightLoading));
    },
    [dispatch, flight, legNumber, setFlightLoading]
  );
  function getDisplayNamesFromBrands(brands) {
    return Object.values(
      brands.reduce((acc, cur) => Object.assign(acc, { [cur.LegBrandName]: cur }), {})
    );
  }
  const uniqueBrands = getDisplayNamesFromBrands(brands);
  return (
    <View style={cardStyle.composedRow}>
      <ShrinkView style={CommonStyles.flex1}>
        {leg.AirSegments &&
          leg.AirSegments.map((segment, index) => (
            <AirDetailsItem key={index} segment={segment} leg={leg} flight={flight} />
          ))}
        {leg.AirSegments.length > 1 && (
          <View style={styles.layover}>
            <XSmallText>
              {t(tKey('Duration'))} {leg.TotalDuration}
            </XSmallText>
          </View>
        )}
      </ShrinkView>
      {showBrands && (
        <View style={CommonStyles.ml2}>
          {showPerUnitPrice ? (
            <PerUnitPrice
              label={t(tKey('PerPerson'))}
              value={formatCurrency(
                isBXcurrency(currencyCulture) ? flight.TotalPoints : flight.TotalFare,
                isBXcurrency(currencyCulture) ? '' : currency,
                currencyCulture
              )}
            />
          ) : (
            uniqueBrands?.map((brand, index) => (
              <PriceItem
                key={index}
                price={formatCurrency(
                  isBXcurrency(currencyCulture) ? brand.TotalPoints : brand.TotalFare,
                  isBXcurrency(currencyCulture) ? '' : currency,
                  currencyCulture
                )}
                labelUp={t('air:FlightScreen.' + customBrandName(brand.DisplayName))}
                isPrimary={flight.selectedBrand ? flight.selectedBrand === index : index === 0}
                iconName={getBrandDetails(brand).icon}
                disabled={!isAirBrandEnabled(brand)}
                onPress={() => onBrandPressHandler(brand)}
                policyReasonCode={brand.ReasonCode?.toLowerCase()}
              />
            ))
          )}
        </View>
      )}
    </View>
  );
};

const AirCard = ({
  flight = {},
  legIndex = 0,
  showBrands,
  showPerUnitPrice,
  currency,
  currencyCulture,
  ...rest
}) => {
  const [data, setData] = useState(null);
  const [defaultBrand, setDefaultBrand] = useState(null);
  const { tKey } = useComponentTKey('FlightScreen');
  const { t } = useTranslation(i18nNS.air);

  useEffect(() => {
    if (Object.keys(flight).length > 0) {
      const leg = flight?.AirLegs?.[legIndex];
      if (Object.keys(leg).length > 0) {
        setData(transformAirDataToCard(flight, legIndex, currency, currencyCulture));
      }

      let brandName = '';
      if (flight.selectedBrand) {
        brandName = flight.MultiBrands[flight.selectedBrand].LegBrandName;
      } else if (leg.AirLegBrandName) {
        brandName = leg.AirLegBrandName;
      } else if (leg?.AirSegments?.[0]?.AirCabinClass) {
        brandName = leg.AirSegments[0].AirCabinClass;
      }

      // this block has been created to handle bx flow brand
      //so this block does not affect normal flows
      if (isBXcurrency(currencyCulture)) {
        if (flight.selectedBrand >= 0) {
          brandName = flight.MultiBrands[flight.selectedBrand].LegBrandName;
        } else if (leg.AirLegBrandName) {
          brandName = leg.AirLegBrandName;
        } else if (leg?.AirSegments?.[0]?.AirCabinClass) {
          brandName = leg.AirSegments[0].AirCabinClass;
        }
      }
      //AA block finishes here

      if (leg?.AirlineCode?.toLowerCase() === 'wn') {
        brandName = brandName.split(' ')[0].toLowerCase();
        if (brandName === 'business') {
          brandName = 'first_wn';
        } else if (brandName === 'select') {
          brandName = 'select_wn';
        } else if (brandName === 'main') {
          brandName = 'wga_wn';
        }
      }
      if (brandName) {
        setDefaultBrand(getBrandDetailsByName(brandName.split(' ')[0]));
      }
    }
  }, [legIndex, flight]);

  return data ? (
    <Card
      title={data.title}
      titleExtra={data.titleExtra}
      subtitle={data.subtitle}
      value={data.value}
      textDownValue={data.textDownValue}
      icon={data.icon}
      textUpIcon={data.textUpIcon}
      textDownIcon={data.textDownIcon}
      policy={data.policy}
      uppercaseSubtitle={true}
      alignCenterContent={true}
      expandInsteadOfPress={data.expandInsteadOfPress}
      isAirCard={true}
      detail={
        <AirDetails
          legNumber={rest.legNumber}
          setFlightLoading={rest.setFlightLoading}
          flight={flight}
          legIndex={legIndex}
          showBrands={showBrands}
          showPerUnitPrice={showPerUnitPrice}
          currency={currency}
          currencyCulture={currencyCulture}
        />
      }
      {...rest}
    >
      <RowView>
        {defaultBrand && (
          <View style={CommonStyles.centerItems}>
            <Icon style={styles.iconBrand} name={defaultBrand.icon} />
            <XSmallText style={cardStyle.grayColor}>{t(tKey(defaultBrand.key))}</XSmallText>
          </View>
        )}

        {flight.IsCorporate && <Icon style={styles.corporate} name="corporate" />}
      </RowView>
    </Card>
  ) : (
    <></>
  );
};

const styles = StyleSheet.create({
  iconBrand: {
    fontSize: FontSize.h2,
    color: Color.primary,
    ...CommonProperties.pb2,
  },
  corporate: {
    color: Color.corporate,
    ...CommonProperties.small,
  },
  mark: {
    backgroundColor: Color.corporate,
    color: Color.accent,
    borderRadius: FontSize.xSmall,
    ...CommonProperties.py1,
    ...CommonProperties.px3,
    ...CommonProperties.xxSmall,
    ...CommonProperties.centerAlignment,
  },
  layover: {
    backgroundColor: 'rgb(245,245,245)',
    alignItems: 'center',
    ...CommonProperties.borderRadius,
    ...CommonProperties.py2,
    ...CommonProperties.mt2,
  },
});

export default AirCard;
